<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'vuex';
import Modal from 'dashboard/components/Modal.vue';
import orderIssuesAPI from 'dashboard/api/orderIssues';
import useOrderIssueTags from 'dashboard/composables/useOrderIssueTags';
import Multiselect from 'vue-multiselect';
import ShopifyAPI from 'dashboard/api/integrations/shopify';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  orderIssue: {
    type: Object,
    default: null,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
  conversationDisplayId: {
    type: [Number, String],
    default: null,
  },
  createMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();
const store = useStore();

const normalizeConversationIdentifier = value => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const withoutHash = trimmed.startsWith('#')
      ? trimmed.slice(1)
      : trimmed;
    return withoutHash.length ? withoutHash : null;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : String(value);
  }

  return null;
};

const sanitizeConversationId = value => {
  const normalized = normalizeConversationIdentifier(value);
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const formData = ref({
  order_number: '#X',
  problem_tag: '',
  action_tag: '',
  note: '',
  conversation_id: null,
});

const loading = ref(false);
const loadingExisting = ref(false);
const existingOrderIssue = ref(null);

// Use centralized tag management with template-based filtering
const {
  selectedProblemTag,
  selectedActionTag,
  filteredProblemTagOptions,
  filteredActionTagOptions,
  addProblemTag,
  addActionTag,
  setTags,
  getTagValues,
} = useOrderIssueTags();

// Force create mode = never edit, only create
const isEditMode = computed(() => {
  if (props.createMode) return false;
  return !!props.orderIssue || !!existingOrderIssue.value;
});

const modalTitle = computed(() =>
  isEditMode.value
    ? t('ORDER_ISSUES.MODAL.EDIT_TITLE')
    : t('ORDER_ISSUES.MODAL.CREATE_TITLE')
);

// Fetch existing order issue for this conversation (only when opened from conversation panel)
const fetchExistingOrderIssue = async () => {
  // Skip if createMode is enabled - always create new
  if (props.createMode) {
    existingOrderIssue.value = null;
    return;
  }
  
  // Skip if we already have an orderIssue prop (editing from order issues page)
  if (props.orderIssue) {
    // console.log('📋 Using orderIssue prop (edit mode from Order Issues page)');
    return;
  }
  
  if (!props.conversationId) {
    // console.log('⚠️ No conversationId prop, skipping fetch');
    return;
  }
  
  loadingExisting.value = true;
  // console.log('🔍 Fetching order issue for conversation ID:', props.conversationId);
  
  const candidateIdentifiers = [
    props.orderIssue?.conversation?.id,
    props.orderIssue?.conversation?.display_id,
    props.orderIssue?.conversation_id,
    props.conversationId,
    props.conversationDisplayId,
  ]
    .map(normalizeConversationIdentifier)
    .filter((identifier, index, self) => identifier && self.indexOf(identifier) === index);

  // console.log('🔎 Candidate conversation identifiers:', candidateIdentifiers);

  let fetchedIssue = null;
  let lastError = null;

  for (const identifier of candidateIdentifiers) {
    try {
      const response = await orderIssuesAPI.getByConversation(identifier);
     // console.log(`📡 API Response for identifier ${identifier}:`, response.data);
      if (response?.data && response.data.id) {
        fetchedIssue = response.data;
        break;
      }
    } catch (error) {
      lastError = error;
      // console.error(`❌ Error fetching order issue for identifier ${identifier}:`, error);
    }
  }

  if (!fetchedIssue && lastError) {
    // console.warn('⚠️ No order issue found. Last error details logged above.');
  }

  existingOrderIssue.value = fetchedIssue;
  loadingExisting.value = false;
};

// Initialize form data when modal opens
const initializeForm = () => {
  // console.log('🔍 InitializeForm - orderIssue prop:', props.orderIssue);
  // console.log('🔍 InitializeForm - conversationId prop:', props.conversationId);

  if (props.orderIssue) {
    formData.value = {
      order_number: props.orderIssue?.order_number || '#X',
      note: props.orderIssue?.note || '',
      conversation_id: sanitizeConversationId(
        props.orderIssue?.conversation?.id ||
          props.orderIssue?.conversation?.display_id ||
          props.orderIssue?.conversation_id
      ),
    };
    // Set tags using composable
    setTags(props.orderIssue?.problem_tag, props.orderIssue?.action_tag);
    // console.log('📋 Loaded orderIssue prop for editing');
  } else if (existingOrderIssue.value && !props.createMode) {
    // Editing existing order issue fetched by conversation id
    formData.value = {
      order_number: existingOrderIssue.value.order_number || '#X',
      note: existingOrderIssue.value.note || '',
      conversation_id: sanitizeConversationId(
        existingOrderIssue.value.conversation?.id ||
          existingOrderIssue.value.conversation?.display_id ||
          existingOrderIssue.value.conversation_id
      ),
    };
    // Set tags using composable
    setTags(existingOrderIssue.value.problem_tag, existingOrderIssue.value.action_tag);
    // console.log('✅ Loaded conversation order issue for editing');
  } else {
    // New order issue from conversation panel
    // Try to fetch order number from Shopify
    fetchOrderNumberFromShopify().then(orderNumber => {
      formData.value = {
        order_number: orderNumber || '#X',
        note: '',
        conversation_id: sanitizeConversationId(props.conversationId),
      };
    });
    // Clear tags
    setTags(null, null);
    // console.log('✅ New order issue form');
  }

  // console.log('✅ Form data:', formData.value);
};

const handleSave = async () => {
  // Validate conversation_id is present
  if (!formData.value.conversation_id) {
    useAlert(t('ORDER_ISSUES.MODAL.CONVERSATION_REQUIRED'));
    return;
  }

  loading.value = true;
  try {
    // Get tag values from composable
    const tagValues = getTagValues();
    
    const payload = {
      order_issue: {
        ...formData.value,
        ...tagValues,
      },
    };

    // console.log('💾 Saving order issue with payload:', payload);

    if (isEditMode.value) {
      const targetIssueId = props.orderIssue?.id || existingOrderIssue.value?.id;
      await orderIssuesAPI.update(targetIssueId, payload);
      useAlert(t('ORDER_ISSUES.API.UPDATE_SUCCESS'));
      if (existingOrderIssue.value) {
        existingOrderIssue.value = {
          ...existingOrderIssue.value,
          ...payload.order_issue,
        };
      }
    } else {
      await orderIssuesAPI.create(payload);
      useAlert(t('ORDER_ISSUES.API.CREATE_SUCCESS'));
      existingOrderIssue.value = payload.order_issue;
    }

    // Reset form after successful save
    formData.value = {
      order_number: '#X',
      problem_tag: '',
      action_tag: '',
      note: '',
      conversation_id: null,
    };

    emit('success');
    emit('close');
  } catch (error) {
    // Check if it's a conversation not found error
    const errorMessage = error?.response?.data?.message || error?.message || '';
    if (errorMessage.includes('Conversation must exist') || errorMessage.includes('Conversation')) {
      useAlert(t('ORDER_ISSUES.MODAL.CONVERSATION_NOT_FOUND'));
    } else {
      useAlert(
        isEditMode.value
          ? t('ORDER_ISSUES.API.UPDATE_ERROR')
          : t('ORDER_ISSUES.API.CREATE_ERROR')
      );
    }
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  // Reset form when closing
  formData.value = {
    order_number: '#X',
    problem_tag: '',
    action_tag: '',
    note: '',
    conversation_id: null,
  };
  existingOrderIssue.value = null;
  emit('close');
};

// React when modal visibility changes
watch(
  () => props.show,
  async newValue => {
    if (!newValue) {
      return;
    }

    await fetchExistingOrderIssue();
    initializeForm();
  }
);

watch(
  () => props.conversationId,
  newValue => {
    if (props.show && !existingOrderIssue.value) {
      formData.value.conversation_id = sanitizeConversationId(newValue);
    }
  }
);

watch(
  () => props.conversationDisplayId,
  newValue => {
    if (props.show && !existingOrderIssue.value && !formData.value.conversation_id) {
      formData.value.conversation_id = sanitizeConversationId(newValue);
    }
  }
);

const fetchOrderNumberFromShopify = async () => {
  try {
    // Get current conversation from store
    const conversationId = props.conversationId;
    if (!conversationId) return null;
    
    const conversation = store.getters.getConversationById(conversationId);
    if (!conversation || !conversation.meta || !conversation.meta.sender) return null;
    
    // Get contact ID from conversation
    const contactId = conversation.meta.sender.id;
    if (!contactId) return null;
    
    // Fetch Shopify orders for this contact
    const response = await ShopifyAPI.getOrders(contactId);
    const orders = response.data?.orders || [];
    
    if (orders.length === 0) return null;
    
    // Get the most recent order (orders are already sorted by created_at desc from API)
    const mostRecentOrder = orders[0];
    const orderName = mostRecentOrder.name;
    
    // Shopify order names are like "#1001", return as is or clean up
    if (orderName) {
      return orderName.startsWith('#') ? orderName : `#${orderName}`;
    }
    
    return null;
  } catch (error) {
    console.error('Failed to fetch Shopify order:', error);
    return null;
  }
};
</script>

<template>
  <Modal
    :show="show"
    :on-close="handleClose"
  >
    <div class="flex flex-col">
      <div class="flex items-center justify-between p-4 border-b border-n-weak">
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ modalTitle }}
        </h2>
      </div>

      <div v-if="loadingExisting" class="p-8 flex items-center justify-center">
        <div class="text-n-slate-11">Loading...</div>
      </div>

      <div v-else class="p-4 space-y-4">
        <!-- Conversation ID (always editable, pre-filled if from conversation) -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('ORDER_ISSUES.MODAL.CONVERSATION_ID_LABEL') }} *
          </label>
          <input
            v-model.number="formData.conversation_id"
            type="number"
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            :placeholder="$t('ORDER_ISSUES.MODAL.CONVERSATION_ID_PLACEHOLDER')"
            required
          />
        </div>

        <!-- Order Number -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('ORDER_ISSUES.MODAL.ORDER_NUMBER_LABEL') }}
          </label>
          <input
            v-model="formData.order_number"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            :placeholder="$t('ORDER_ISSUES.MODAL.ORDER_NUMBER_PLACEHOLDER')"
          />
        </div>

        <!-- Problem Tag -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('ORDER_ISSUES.MODAL.PROBLEM_TAG_LABEL') }}
          </label>
          <multiselect
            v-model="selectedProblemTag"
            :options="filteredProblemTagOptions"
            track-by="id"
            label="name"
            :placeholder="$t('ORDER_ISSUES.MODAL.PROBLEM_TAG_PLACEHOLDER')"
            :allow-empty="true"
            :taggable="true"
            @tag="addProblemTag"
          >
            <template #noOptions>
              {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
            </template>
          </multiselect>
        </div>

        <!-- Action Tag -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('ORDER_ISSUES.MODAL.ACTION_TAG_LABEL') }}
          </label>
          <multiselect
            v-model="selectedActionTag"
            :options="filteredActionTagOptions"
            track-by="id"
            label="name"
            :placeholder="$t('ORDER_ISSUES.MODAL.ACTION_TAG_PLACEHOLDER')"
            :allow-empty="true"
            :taggable="true"
            @tag="addActionTag"
          >
            <template #noOptions>
              {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
            </template>
          </multiselect>
        </div>

        <!-- Note -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('ORDER_ISSUES.MODAL.NOTE_LABEL') }}
          </label>
          <textarea
            v-model="formData.note"
            rows="3"
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand text-sm min-h-[80px]"
            :placeholder="$t('ORDER_ISSUES.MODAL.NOTE_PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-2 p-4 border-t border-n-weak">
        <button
          class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="handleClose"
        >
          {{ $t('ORDER_ISSUES.MODAL.CANCEL') }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium text-white bg-n-brand hover:bg-n-brand-dark rounded-md transition-colors disabled:opacity-50"
          :disabled="loading"
          @click="handleSave"
        >
          {{ loading ? '...' : $t('ORDER_ISSUES.MODAL.SAVE') }}
        </button>
      </div>
    </div>
  </Modal>
</template>
